/** Drop blank ids from a Service Center coverage list. */
export function cleanIdList(values?: ReadonlyArray<string | null | undefined> | null): string[] {
    return (values ?? []).filter((id): id is string => Boolean(id && id.trim()));
}

export function nameMap(records: Array<{ id: string; name?: string | null }>): Map<string, string> {
    const map = new Map<string, string>();
    for (const record of records) {
        const name = record.name?.trim();
        if (record.id && name) {
            map.set(record.id, name);
        }
    }
    return map;
}

export interface ServiceCenterCoverage {
    id: string;
    name?: string | null;
    archived?: boolean | null;
    regionIds?: ReadonlyArray<string | null | undefined> | null;
    locationIds?: ReadonlyArray<string | null | undefined> | null;
}

function byName<T extends { name?: string | null }>(centers: T[]): T[] {
    return [...centers].sort((left, right) => (left.name || '').localeCompare(right.name || '', undefined, { sensitivity: 'base' }));
}

/** Active centers only. An archived center is not offered on a location. */
export function activeServiceCenters<T extends ServiceCenterCoverage>(centers: readonly T[]): T[] {
    return byName(centers.filter((center) => center.id && !center.archived));
}

/**
 * The service center a location should show.
 * An explicit location wins, then a center that covers the location's region, then a center that covers every location.
 */
export function serviceCenterIdForLocation(
    location: { id: string; regionalDivisionId?: string | null },
    centers: readonly ServiceCenterCoverage[],
): string | null {
    const active = activeServiceCenters(centers);
    const explicit = active.filter((center) => cleanIdList(center.locationIds).includes(location.id));
    if (explicit.length > 0) return explicit[0].id;

    const regionId = location.regionalDivisionId?.trim() || '';
    const byRegion = active.filter((center) => {
        const locationIds = cleanIdList(center.locationIds);
        const regionIds = cleanIdList(center.regionIds);
        return locationIds.length === 0 && Boolean(regionId) && regionIds.includes(regionId);
    });
    if (byRegion.length > 0) return byRegion[0].id;

    const coversAll = active.filter(
        (center) => cleanIdList(center.locationIds).length === 0 && cleanIdList(center.regionIds).length === 0,
    );
    return coversAll[0]?.id ?? null;
}

/**
 * Location ids to store on one service center after a location's dropdown changes.
 * A center that already covers every location (empty list) is not narrowed to one id.
 * Choosing another center removes this location from an explicit list.
 */
export function locationIdsForAssignment(
    center: ServiceCenterCoverage,
    locationId: string,
    selectedCenterId: string | null,
): string[] {
    const ids = cleanIdList(center.locationIds);
    const listed = ids.includes(locationId);
    if (center.id === selectedCenterId) {
        if (ids.length === 0 || listed) return ids;
        return [...ids, locationId];
    }
    if (!listed) return ids;
    return ids.filter((id) => id !== locationId);
}

/** Comma-separated names for the ids that still exist in the lookup. */
export function namesForIds(ids: string[], names: ReadonlyMap<string, string>): string {
    return ids
        .map((id) => names.get(id))
        .filter((name): name is string => Boolean(name))
        .join(', ');
}
